import React from 'react';
import { CheckCircle2, Circle, X, AlertTriangle } from 'lucide-react';
import { Subtask } from '../../types/todo';

interface TaskSubtasksProps {
  taskId: string;
  subtasks: Subtask[];
  isPreview: boolean;
  onToggleSubtask: (taskId: string, subtaskId: string) => void;
  onDeleteSubtask: (taskId: string, subtaskId: string) => void;
}

export function TaskSubtasks({
  taskId,
  subtasks,
  isPreview,
  onToggleSubtask,
  onDeleteSubtask
}: TaskSubtasksProps) {
  const [subtaskToDelete, setSubtaskToDelete] = React.useState<{id: string; title: string} | null>(null);

  const handleConfirmDelete = () => {
    if (subtaskToDelete) {
      onDeleteSubtask(taskId, subtaskToDelete.id);
      setSubtaskToDelete(null);
    }
  };

  return (
    <>
      <div className="space-y-0.5 sm:space-y-1">
        {subtasks.map(subtask => (
          <div
            key={subtask.id}
            className="flex items-center gap-2.5 sm:gap-2 py-2.5 sm:py-1.5 px-2 sm:px-2 rounded-xl sm:rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 active:bg-gray-100 dark:active:bg-gray-700/50 transition-colors group"
          >
            <button
              onClick={() => onToggleSubtask(taskId, subtask.id)}
              className="flex-shrink-0 p-1 -m-1 touch-manipulation"
              disabled={isPreview}
            >
              {subtask.completed ? (
                <CheckCircle2 className="w-5 h-5 sm:w-4 sm:h-4 text-emerald-500" />
              ) : (
                <Circle className="w-5 h-5 sm:w-4 sm:h-4 text-gray-300 dark:text-gray-600" />
              )}
            </button>
            <span className={`flex-1 text-sm ${
              subtask.completed
                ? 'line-through text-gray-400 dark:text-gray-500'
                : 'text-gray-700 dark:text-gray-300'
            }`}>
              {subtask.title}
            </span>
            {!isPreview && (
              <button
                onClick={() => setSubtaskToDelete({ id: subtask.id, title: subtask.title })}
                className="sm:opacity-0 sm:group-hover:opacity-100 p-2 sm:p-0.5 -mr-1 sm:mr-0 text-gray-400 hover:text-red-500 active:text-red-600 rounded-lg sm:rounded transition-all touch-manipulation"
              >
                <X className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>

      {subtaskToDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-sm safe-area-bottom">
            <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mt-3 sm:hidden" />
            <div className="p-5 sm:p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 sm:w-10 sm:h-10 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Supprimer cette sous-tache ?
                </h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                "{subtaskToDelete.title}" sera supprimee definitivement.
              </p>
              <div className="flex gap-2.5 sm:gap-2">
                <button
                  onClick={() => setSubtaskToDelete(null)}
                  className="flex-1 px-4 py-3 sm:py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 active:bg-gray-300 rounded-xl transition-colors touch-manipulation"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="flex-1 px-4 py-3 sm:py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 active:bg-red-800 rounded-xl transition-colors touch-manipulation"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
